










import { Component, Vue } from 'vue-property-decorator';
import User from '@/models/User';
import Location from '@/models/Location';
import { namespace } from 'vuex-class';
import { LocalizationResponse } from '@/interfaces/LocalizationResponse';
import { Dictionary } from 'vue-router/types/router';
import isMobile from 'ismobilejs';
import { v4 as uuidv4 } from 'uuid';

const AuthStore = namespace('auth');
const ApplicationStore = namespace('application');
const MagazineStore = namespace('magazine');
const TrackingStore = namespace('tracking');

@Component({
    components: {
        LocationDialogComponent: () => import(
            /* webpackChunkName: "LocationDialogComponent" */
            '@/components/shared/LocationDialog.component.vue'),
        TermsAndConditionsDialogComponent: () => import(
            /* webpackChunkName: "TermsAndConditionsDialogComponent" */
            '@/components/shared/TermsAndConditionsDialog.component.vue'),
        InvalidTokenDialogComponent: () => import(
            /* webpackChunkName: "InvalidTokenDialogComponent" */
            '@/components/shared/InvalidTokenDialog.component.vue'),
        InactivityDialogComponent: () => import(
            /* webpackChunkName: "InactivityDialogComponent" */
            '@/components/shared/InactivityDialog.component.vue')
    }
})
export default class RootView extends Vue {

    @AuthStore.Action('login')
    private loginAction!: () => Promise<User>;

    @AuthStore.Action('logout')
    private logoutAction!: () => Promise<void>;

    @AuthStore.Action('initializeUser')
    private initializeUserAction!: () => Promise<User>;

    @AuthStore.Mutation('setToken')
    private setToken!: (token?: string) => void;

    @AuthStore.Action('localization')
    private localizationAction!: (id_location?: string) => Promise<LocalizationResponse>;

    @AuthStore.Action('confirmLocation')
    private confirmLocationAction!: (id_location?: string) => Promise<Location>;

    @AuthStore.Mutation('saveLocation')
    private saveLocation!: (location: Location | null) => void;

    @AuthStore.Getter('currentLocation')
    private currentLocation!: Location | null;

    @AuthStore.Mutation('setAvailableLocations')
    private setAvailableLocations!: (locations: Location[]) => void;

    @AuthStore.Getter('localizationTimeOut')
    private _localizationTimeOut!: boolean;

    get localizationTimeOut(): boolean {
        return this._localizationTimeOut;
    }

    @AuthStore.Mutation('setLocalizationTimeOut')
    private setLocalizationTimeOut!: (value: boolean) => void;

    @AuthStore.Mutation('setBridge')
    private setBridge!: (value: string) => void;

    @ApplicationStore.Mutation('setLocationDialog')
    private setLocationDialog!: (value: boolean) => void;

    @ApplicationStore.Getter('accessibilityControls')
    private accessibilityControls!: boolean | null;

    @ApplicationStore.Mutation('setAccessibilityControls')
    private setAccessibilityControls!: (value: boolean) => void;

    @ApplicationStore.Getter('termsAndConditionsConfirmed')
    private termsAndConditionsConfirmed!: boolean | null;

    @ApplicationStore.Mutation('setTermsAndConditionsConfirmed')
    private setTermsAndConditionsConfirmed!: (value: boolean) => void;

    @ApplicationStore.Mutation('setCategoryMenu')
    private setCategoryMenu!: (value: boolean) => void;

    @ApplicationStore.Mutation('setTermsAndConditionsDialog')
    private setTermsAndConditionsDialog!: (value: boolean) => void;

    @ApplicationStore.Mutation('setInvalidTokenDialog')
    private setInvalidTokenDialog!: (value: boolean) => void;

    @ApplicationStore.Getter('usingCode')
    private usingCode!: boolean;

    @MagazineStore.Action('getCategories')
    private getCategoriesAction!: () => Promise<void>;

    @MagazineStore.Action('getLocationMagazines')
    private getLocationMagazinesAction!: (locationId?: string) => Promise<any>;

    @TrackingStore.Action('sendTracking')
    private sendTracking!: (isOfflineData?: boolean) => Promise<void>;

    @TrackingStore.Mutation('clearTracking')
    private clearTracking!: () => void;

    /**
     * Url Params for ident Token, theme token usw.
     */
    public queryParams?: Dictionary<string | (string | null)[]>;

    /**
     * Counts the failed localization attemps
     */
    private failedLocalizationCounter: number = 0;

    public async created() {
        // get url tokens
        this.queryParams = this.$route.query;
        // save token
        if (this.queryParams?.ident) {
            this.setToken(this.queryParams.ident as string);
        }
        // saves a bridge value. If a bridge value is set, it will be send in each
        // request and enables the use of the web reader inside an iframe in safari.
        if (this.queryParams && 'bridge' in this.queryParams && this.isInIframe()) {
            this.setBridge(uuidv4());
        }
        // remove query params
        this.$router.replace({ name: 'library_view' })
            // eslint-disable-next-line handle-callback-err
            .catch(() => {
                /* do nothing (without this a NavigationDuplicated Error is thrown) */
            });
        // handle accessibility controls
        this.handleAccessibilityControls();
        // handle terms and condition confirmation
        if (process.env.VUE_APP_SHOW_ROOM_MODE !== 'true') {
            this.handleTermsAndConditionConfirmation();
        }

        // DO NOT ASK WHY... BUT THE USING OF async/await DOES NOT GUARANTEE THE RIGHT EXECUTION ORDER
        this.initializeUserAction()
            // logout to make old session invalid
            .then(() => {
                    if (this.queryParams?.ident) {
                        return this.logoutAction();
                    }
                }
            )
            .then(() => this.loginAction())
            .then(async () => {
                // - do diverse stuff after login
                // -- make promise unimportant stuff
                await this.sendTracking(true);
                this.clearTracking();

                // -- make promise important requests (the promise is important for error handling)
                // Test for debug
                const locationId = this.currentLocation ? this.currentLocation.id : undefined;
                const promises = [
                    this.getLocationMagazinesAction(locationId),
                    this.getCategoriesAction()
                ];
                if (this.termsAndConditionsConfirmed === true && process.env.VUE_APP_SHOW_ROOM_MODE !== 'true') {
                    this.startLocalizationInterval();
                }
                await Promise.all(promises);
                // show category menu if possible
                this.setCategoryMenu(process.env.VUE_APP_DEFAULT_CATEGORY_MENU_STATE! === 'true');
            })
            .catch(e => {
                console.log(e);
                Vue.notifyErrorSimplified('GENERAL.NOTIFICATIONS.SERVER_ERROR');
            }).finally(() => {
            this.queryParams = undefined;
        });
    }

    /**
     * Start the localization interval
     */
    private async startLocalizationInterval() {
        console.info('start localization with interval: ', Number(process.env.VUE_APP_LOCALIZATION_INTERVAL!));
        // make a initial localization attemp
        await this.performLocalization();
        // start interval
        setInterval(async () => {
            if (!this.localizationTimeOut) {
                await this.performLocalization();
            }
        }, Number(process.env.VUE_APP_LOCALIZATION_INTERVAL!));
    }

    /**
     * Performs a single localization attempt
     */
    private async performLocalization() {
        try {
            // try to localize with geo position / ip
            // or try to confirm location with geo position / ip and current location
            const localizationResponse: LocalizationResponse = await this.localizationAction();
            /**
             * The following values can be returned:
             *      code: 511, "you are in the given location."
             *      -> do nothing
             *      code: 501, "you are near a location."
             *      -> show location select (if just one location available, confirm this location immediately)
             *      code: 510, "you are not in the given location."
             *      -> reset current location
             *      code: 500, "you are not near a location."
             *      - reset current location
             *      code: 520, "your token is invalid"
             *      - reset current location
             */
            switch (localizationResponse.code) {
                case 501:
                    if (localizationResponse.locations!.length === 1) {
                        await this.confirmLocationAction(localizationResponse.locations![0].id);
                        if (this.currentLocation) {
                            await this.getLocationMagazinesAction(this.currentLocation.id);
                        }
                    } else {
                        // opens the location select dialog
                        this.setAvailableLocations(localizationResponse.locations!);
                        this.setLocationDialog(true);
                    }
                    // reset counter
                    this.failedLocalizationCounter = 0;
                    break;
                case 510:
                    // check for other available locations immediately to avoid the waiting for the next
                    // localization interval. If then no location is available, code 500 will be returned.
                    this.saveLocation(null);
                    await this.performLocalization();
                    // this occurs if only one location is available an used automatically
                    if (this.currentLocation) {
                        this.$notifyInfoSimplified('LOCALIZATION.LOCATION_CHANGE');
                    } else {
                        // reset magazines
                        await this.getLocationMagazinesAction();
                    }
                    break;
                case 520:
                    this.setToken(undefined);
                    if (this.usingCode) {
                        this.setInvalidTokenDialog(true);
                    }
                    this.handleFailedLocalization();
                    await this.resetLocalizationHandling();
                    break;
                case 500:
                    this.handleFailedLocalization();
                    await this.resetLocalizationHandling();
                    break;
            }
        } catch (e) {
            console.error('localization could not be performed', e);
            // normally it is not possible that an error occurs....
            await this.resetLocalizationHandling();
        }
    }

    /**
     * Resets localization handling values
     */
    private async resetLocalizationHandling() {
        if (this.currentLocation) {
            await this.getLocationMagazinesAction();
            this.saveLocation(null);
        }
        this.setAvailableLocations([]);
        this.setLocationDialog(false);
    }

    /**
     * Handles failed localization attempts
     */
    private handleFailedLocalization() {
        this.failedLocalizationCounter++;

        if (!this.localizationTimeOut &&
            Number(process.env.VUE_APP_LOCALIZATION_TIME_OUT_TRIES) <= this.failedLocalizationCounter) {
            this.setLocalizationTimeOut(true);
        }
    }

    /**
     * Determines if controls should be shown or not. Controls should not be shown if device has touch or
     * if the query param defines this.
     */
    public handleAccessibilityControls() {
        // !== null means the user did not set the controls by hand
        if (this.accessibilityControls === null) {
            if (this.queryParams && 'controls' in this.queryParams) {
                const value = this.queryParams.controls === 'true' || this.queryParams.controls === null;
                this.setAccessibilityControls(value);
            } else {
                // set controls if device is not mobile
                const isMobileDevice = isMobile(window.navigator).any;
                this.setAccessibilityControls(!isMobileDevice);
            }
        }
    }

    /**
     * TAC (Terms And Conditions) handler
     */
    private handleTermsAndConditionConfirmation() {
        if (this.termsAndConditionsConfirmed !== true) {
            if (this.queryParams && 'noTac' in this.queryParams) {
                const value = !(this.queryParams.noTac === 'true' || this.queryParams.noTac === null);
                this.setTermsAndConditionsDialog(value);
                if (!value) {
                    this.setTermsAndConditionsConfirmed(true);
                }
            } else {
                this.setTermsAndConditionsDialog(true);
            }
        }
    }

    /**
     * TAC (Terms And Conditions) confirmation handler
     */
    private onConfirmTermsAndConditions() {
        this.setTermsAndConditionsDialog(false);
        this.setTermsAndConditionsConfirmed(true);
        if (process.env.VUE_APP_SHOW_ROOM_MODE !== 'true') {
            this.startLocalizationInterval();
        }
    }

    /**
     * TAC (Terms And Conditions) rejected handler
     */
    private onRejectTermsAndConditions() {
        this.setTermsAndConditionsDialog(false);
        this.setTermsAndConditionsConfirmed(false);
        this.setLocalizationTimeOut(true);
    }

    /**
     * Check if web reader is in iframe
     * @private
     */
    private isInIframe(): boolean {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }
}
